/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useResizeObserver } from '../_internal/hooks';
import { useIsomorphicLayoutEffect } from '../hooks';
import styles from './styles/base.module.css';
import { Caption } from '../caption';
import { Label } from './label';
import { ClearButton } from './clear-button';
import { isBrowser, mergeRefs } from '../utils';
import { getMaxLengthMessage } from './get-max-length-message';
/**
 * `<Textarea />` Текстовые поля позволяют пользователям вводить текст в пользовательский интерфейс
 *
 * @example ``` tsx
 *  <Textarea onChange={() => {}} value="text" />
 * ```
 */
export const Textarea = /*#__PURE__*/React.forwardRef(function Textarea(props, ref) {
  const {
    label,
    value,
    onBlur,
    captionMessage,
    caption,
    onFocus,
    onChange,
    onInput,
    disabled,
    className,
    isError,
    size = 'l',
    maxLength,
    autoHeight,
    ...textareaProps
  } = props;
  const textareaRef = useRef(null);
  const resetIconRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [scrollBarClassName, setScrollBarClassName] = useState('');
  const textareaRootClass = useMemo(() => clsx(styles.textareaRoot, className, styles[size], {
    [styles.textareaError]: isError,
    [styles.textareaDisabled]: disabled,
    [styles.hasLabel]: !!label,
    [styles.textareaFocused]: focused
  }), [className, size, isError, disabled, label, focused]);
  const fieldListClassName = useMemo(() => clsx(styles.textareaFieldList, {
    [styles.textareaFieldListWithLabel]: !!label
  }), [label]);
  const onChangeTextarea = useCallback(e => {
    onScroll();
    onChange == null ? void 0 : onChange(e, e.target.value);
  }, [onChange]);
  const [height, setHeight] = useState(autoHeight ? 0 : undefined);
  const onInputTextarea = useCallback(e => {
    onInput == null ? void 0 : onInput(e);
    if (autoHeight) {
      setHeight(0);
    }
  }, [onInput, autoHeight]);
  const onScroll = () => {
    if (textareaRef.current) {
      const hasScroll = textareaRef.current.scrollHeight > textareaRef.current.clientHeight;
      const isMacOsScroll = textareaRef.current.offsetWidth === textareaRef.current.clientWidth;
      if (hasScroll) {
        setScrollBarClassName(isMacOsScroll ? styles.textareaWithScrollbarMacOs : styles.textareaWithScrollbar);
      } else {
        setScrollBarClassName('');
      }
    }
  };
  useResizeObserver(textareaRef, () => {
    if (autoHeight) {
      setHeight(0);
    }
  });
  useIsomorphicLayoutEffect(() => {
    if (autoHeight && !height && textareaRef.current) {
      setHeight(textareaRef.current.scrollHeight);
    }
  }, [height, autoHeight]);
  const onFocusTextarea = useCallback(e => {
    if (focused) return;
    setFocused(true);
    onFocus == null ? void 0 : onFocus(e);
  }, [focused, onFocus]);
  const onBlurTextarea = useCallback(e => {
    var _e$relatedTarget;
    if (((_e$relatedTarget = e.relatedTarget) == null ? void 0 : _e$relatedTarget.parentNode) === resetIconRef.current || !focused) return;
    setFocused(false);
    onBlur == null ? void 0 : onBlur(e);
  }, [focused, onBlur]);
  const onClear = useCallback(() => {
    if (!textareaRef.current) return;
    if (isBrowser()) {
      var _Object$getOwnPropert, _Object$getOwnPropert2;
      (_Object$getOwnPropert = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')) == null ? void 0 : (_Object$getOwnPropert2 = _Object$getOwnPropert.set) == null ? void 0 : _Object$getOwnPropert2.call(textareaRef.current, '');
    }
    textareaRef.current.dispatchEvent(new Event('change', {
      bubbles: true,
      cancelable: true
    }));
    textareaRef.current.focus();
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: textareaRootClass
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx({
      [styles.textareaWrapper]: true,
      [scrollBarClassName]: !!scrollBarClassName
    }),
    "data-testid": "wrapperTextareaTestId"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: mergeRefs([textareaRef, ref]),
    className: clsx({
      [styles.textarea]: true,
      [styles.textareaAutoHeight]: autoHeight
    }),
    value: value == null ? void 0 : value.slice(0, maxLength),
    "data-testid": "textareaTestId",
    disabled: disabled,
    onBlur: onBlurTextarea,
    onFocus: onFocusTextarea,
    onChange: onChangeTextarea,
    onInput: onInputTextarea,
    onScroll: onScroll,
    cols: 1,
    spellCheck: false,
    maxLength: maxLength,
    ...textareaProps,
    style: {
      height,
      ...textareaProps.style
    }
  }), /*#__PURE__*/React.createElement("div", {
    ref: resetIconRef,
    /**
     * FRONTON-685 Элемент скрыт css т.к. при динамическом добавлении в DOM на
     * iOS "залипает" фокус на textarea.
     */
    className: clsx({
      [styles.textareaResetIcon]: true,
      [styles.hide]: !value || !focused
    })
  }, /*#__PURE__*/React.createElement(ClearButton, {
    onClick: onClear
  })), /*#__PURE__*/React.createElement("fieldset", {
    className: fieldListClassName
  }, /*#__PURE__*/React.createElement(Label, {
    label: label
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.bottomInfo
  }, captionMessage && (/*#__PURE__*/React.createElement(Caption, {
    error: isError,
    className: styles.caption,
    captionSize: "l",
    message: captionMessage
  })), caption, maxLength !== undefined && (/*#__PURE__*/React.createElement(Caption, {
    className: styles.symbolCount,
    message: getMaxLengthMessage(maxLength, value),
    "data-testid": "textareaCharLength"
  }))));
});